import {
  IExperienceTemplateResource,
  StorageContext,
  SubTaskHook,
  SubTaskHookNotifyDataDestination,
  SubTaskHookType
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { Typography } from '@material-ui/core';
import { useMemo } from 'react';
import { FormRowGroup } from '../../../../../../../../components/Form/FormRow';
import { TokenDefinition } from '../../../../../../../../components/MarkdownText';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import { FlexContainer } from '../../../../../../../../layout/Flex';
import { useNewUx } from '../../../../../../../../services/newUx';
import { BlockSettingsSubPage, TaskBuilderContext } from '../../../types';
import { TaskBuilderBlockValidationStateItem } from '../../../validation';
import { StorageDestinations } from './dataDestinations';

const OnCompleteDataDestinatons = ({
  value,
  onChange,
  validation,
  // setValidation,
  expTpl,
  customFields,
  resources,
  onChangeResources,
  goToSubPage,
  tokens,
  storageContext,
  defaultCompletionText
}: {
  value: SubTaskHookNotifyDataDestination;
  onChange?: (nextValue: SubTaskHookNotifyDataDestination) => void;
  validation?: TaskBuilderBlockValidationStateItem | null;
  // setValidation: React.Dispatch<
  //   SetStateAction<TaskBuilderBlockValidationState>
  // >;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  resources: IExperienceTemplateResource[];
  onChangeResources?: (nextResources: IExperienceTemplateResource[]) => void;
  goToSubPage?: (nextSubPage: BlockSettingsSubPage) => void;
  tokens: TokenDefinition[];
  storageContext: StorageContext;
  defaultCompletionText?: string;
}) => {
  const { t } = useUnsafeTranslation();
  // Gated by the per-user NEW_UX flag, so classic users' output is unchanged.
  const newUx = useNewUx().enabled;

  return (
    // Automation action settings render this inside a flex column that does
    // not stretch its children, so the group has to claim the full width itself.
    <FormRowGroup style={newUx ? { alignSelf: 'stretch' } : undefined}>
      <FlexContainer alignItems="baseline">
        <Typography
          variant="h5"
          gutterBottom
          style={
            newUx
              ? {
                  fontSize: 14,
                  fontWeight: 400,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: '#778088'
                }
              : undefined
          }
        >
          {t('Data Destinations')}
        </Typography>
        <Typography
          variant="caption"
          style={newUx ? { color: '#778088' } : undefined}
        >
          {t('Optional')}
        </Typography>
      </FlexContainer>
      <Typography
        variant={newUx ? 'caption' : 'body1'}
        color="textSecondary"
        style={newUx ? { color: '#778088', display: 'block' } : undefined}
      >
        {t('Write to data destinations when the sub-task is completed')}
      </Typography>
      <StorageDestinations
        isCompletionHookDataDestination={true}
        tokens={tokens}
        disallowUserProfile={false}
        value={value.storages}
        onChange={
          onChange &&
          ((nextStorages) => {
            console.log(nextStorages);
            onChange({
              ...value,
              storages: nextStorages
            });
          })
        }
        resources={resources}
        onChangeResources={onChangeResources}
        expTpl={expTpl}
        customFields={customFields}
        goToSubPage={goToSubPage}
        validation={validation}
        storageContext={storageContext}
        defualtCompletionText={defaultCompletionText}
      />
    </FormRowGroup>
  );
};

// Add this point in time there can only be one type of onCompleteHook, which deals
// with data destinations
const useNotifyDataDestinationHook = (value: SubTaskHook[] | undefined) => {
  return useMemo(() => {
    for (const h of value || []) {
      if (h.type === SubTaskHookType.NOTIFY_DATA_DESTINATION) {
        return h;
      }
    }
    return {
      type: SubTaskHookType.NOTIFY_DATA_DESTINATION,
      storages: []
    };
  }, [value]);
};

export const OnCompleteHooksReadOnly = ({
  value,
  ...props
}: {
  value: SubTaskHook[] | undefined;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  resources: IExperienceTemplateResource[];
  tokens: TokenDefinition[];
  storageContext: StorageContext;
}) => {
  const notifyDataDestinationHook = useNotifyDataDestinationHook(value);

  if (
    !notifyDataDestinationHook ||
    !notifyDataDestinationHook.storages.length
  ) {
    return null;
  }
  return (
    <OnCompleteDataDestinatons value={notifyDataDestinationHook} {...props} />
  );
};

export const OnCompleteHooks = ({
  value,
  onChange,
  showOnCompleteHookSettings,
  ...props
}: {
  value: SubTaskHook[] | undefined;
  onChange: (nextValue: SubTaskHook[]) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  // setValidation: React.Dispatch<
  //   SetStateAction<TaskBuilderBlockValidationState>
  // >;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
  storageContext: StorageContext;
  defaultCompletionText?: string;
}) => {
  const notifyDataDestinationHook = useNotifyDataDestinationHook(value);

  if (!notifyDataDestinationHook) {
    return null;
  }
  return (
    <>
      {showOnCompleteHookSettings && (
        <OnCompleteDataDestinatons
          value={notifyDataDestinationHook}
          onChange={(nextHook) => onChange([nextHook])}
          {...props}
        />
      )}
    </>
  );
};
